import { useId, useState, type ReactNode } from "react";
import { ConfirmModal } from "@a2z/ui";
import { Link, useLocation } from "react-router-dom";
import { locales, useI18n, useTranslate } from "@/lib/i18n";
import { usePosTheme } from "@/shared/posTheme";
import { useApplyDashboardTheme } from "@/app/themes/dashboardTheme";
import { useApplyTillTextSize } from "@/shared/tillTextSize";
import { RestaurantNotificationBell } from "@/modules/notifications/NotificationBell";
import { canUseBackOffice } from "@/modules/auth/backOffice";
import { useLogout } from "@/modules/auth/useLogout";
import { useSession } from "@/modules/auth/useSession";

/**
 * App-local UI.
 *
 * What is NOT here any more, on purpose: `PrimaryButton`, `SecondaryButton`,
 * `DangerButton`, `Card`, `EmptyState` and `ErrorNote`. Those were app-local
 * reimplementations of primitives `@a2z/ui` already ships, and they silently
 * cost this app every state the shared ones handle — focus, hover, disabled,
 * loading. Screens now import `Button`, `SectionCard` and `EmptyState` from
 * `@a2z/ui` directly; `src/app/styles.css` maps this app's palette onto the
 * token names those primitives read.
 *
 * What stays here is what is genuinely this app's and not shared: the shell,
 * the locale-aware money formatter, and the POS list row.
 *
 * `Field` and `posInputClass` are GONE, and their removal is the point. They
 * were a private form layer sitting beside `FormField` / `TextInput` /
 * `NumberInput` / `Checkbox` in `@a2z/ui`, and the copy paid for itself in
 * every state it did not implement: no focus treatment at all, a 44px/12px-
 * radius/16px control next to the shared `Combobox`'s 35px/6px/13px in the same
 * modal, and a global focus rule bolted on to compensate that drew a black
 * rectangle around every input on the screen. The lint rule in
 * `eslint.no-local-shared-primitives.mjs` now makes rebuilding them an error.
 */

export function Screen({
  title,
  action,
  leading,
  children,
  tone,
  bleed,
  onLogout,
  isLoggingOut,
  phoneHead = false,
}: {
  title: string;
  action?: ReactNode;
  /**
   * Rendered BEFORE the title. For a control that dismisses or returns from
   * the screen: those belong at the start edge, where the eye starts and the
   * artboard puts them, not trailing the page's own controls.
   */
  leading?: ReactNode;
  children: ReactNode;
  /** "dark" = the POS working scope (styles.css `.pos-dark`). Back-office and
   *  auth screens stay light — the till is the only dark room. */
  tone?: "dark";
  /**
   * Hand the whole area below the header to the child, unpadded and unscrolled.
   *
   * For the POS order view, which is a two-pane layout that owns its own
   * scrolling: the menu scrolls independently of the order panel beside it, so
   * a single scroll container around both is exactly wrong. Every other screen
   * wants the default padded, scrolling `main`.
   */
  bleed?: boolean;
  /**
   * Sign out, from a screen that has no shell to do it for it.
   *
   * Only the kitchen board passes this today, and it is the reason the prop
   * exists: the board renders outside `ManageShell`, so the sidebar and drawer
   * that carry sign-out everywhere else are not on the screen, and a cook on a
   * shared wall tablet could never leave their session. The POS screens do not
   * need it — their exit-X reaches the back office, which has both.
   */
  onLogout?: () => void;
  isLoggingOut?: boolean;
  /**
   * WAITER PHONE (founder, 2026-10-28: "we are not supposed to see a
   * breadcrumb and the side icon … identical to the design"). Below `lg` the
   * page draws its OWN one-line head — the table or the floor, then the
   * bell and sign-out (`TillPhoneControls`) — and this top bar is not shown:
   * no crumbs, no back-office icon, no theme / language / sound row. A desk
   * keeps the bar.
   */
  phoneHead?: boolean;
}) {
  const { locale, direction } = useI18n();
  const t = useTranslate();
  const titleId = useId();
  const isTill = tone === "dark";
  // The look the back office wears reaches the till too, light and dark (the
  // dark till is the same look in its dark palette).
  useApplyDashboardTheme(isTill);
  // The device's till text size (D162) — the till's screens only, light and
  // dark; `normal` writes nothing.
  useApplyTillTextSize(isTill);
  /*
   * A FLOOR role's till bar (founder, 2026-10-06: "the waiter needs only
   * notification and logout"). A waiter or a cook has no back office
   * (`modules/auth/backOffice.ts`) — so no sidebar, and with it no sign-out —
   * and no use for the language or light/dark switches, which cost a phone's
   * width. Their bar is the bell (with its sound) and a sign-out of its own.
   */
  const { data: session } = useSession();
  const floorOnly = isTill && session !== undefined && !canUseBackOffice(session);
  const ownLogout = useLogout();
  /*
   * Asked first: on a floor role's bar the sign-out sits next to the bell,
   * where a thumb reaching for one can hit the other — and signing a waiter
   * out mid-service costs them their phone number and password to get back.
   */
  const [isConfirmingLogout, setConfirmingLogout] = useState(false);
  return (
    <div
      className="app-shell"
      dir={direction}
      lang={locale}
      /* Read by `_paper-responsive.css`: on a phone a floor role's bar is a
         floating bell + sign-out, not a full-width row. */
      data-floor-only={floorOnly ? "" : undefined}
      data-phone-head={phoneHead ? "" : undefined}
    >
      <header
        data-ui="topbar"
        className={`flex h-[58px] shrink-0 items-center gap-3 border-b border-[color:var(--border)] bg-[color:var(--surface)] px-4${phoneHead ? " max-lg:hidden" : ""}`}
      >
        {leading ? (
          <span data-ui="topbar-leading" className="contents">
            {leading}
          </span>
        ) : null}
        {/* B117/A5 — THE SCREEN'S OWN NAME DOES NOT SHRINK.
            
            This was `truncate` with no `shrink-0`, so in a flex row competing
            with the summary strip and the till controls it lost: «الطاولات»
            rendered «الطاو…» at 390px in Arabic while the same header fit in
            English. It is the app's home screen and an Arabic-first product,
            and the one string on the bar that cannot be inferred from anything
            else is the name of where you are.
            
            `truncate` stays as the last resort for a genuinely long title; what
            changes is WHO gives way first — the action strip below, which is a
            summary and degrades to a shorter one, rather than the heading. */}
        <h1
          id={titleId}
          data-ui="topbar-title"
          className="shrink-0 truncate text-lg font-semibold"
        >
          {title}
        </h1>
        <div data-ui="topbar-spacer" className="flex-1" />
        {/* `min-w-0` so this is the element that gives, per the note above.

            The padding is SHADOW ROOM, not decoration. `truncate` is
            `overflow: hidden`, and a clipping box clips at its padding box — so
            with no padding this shaved the drawer badge's `3px 3px 0` hard
            shadow off flush on the trailing and bottom edges, which is exactly
            what it looked like: a cropped button. Measured before the fix, the
            badge's right and bottom gaps inside this box were both 0.

            8px on every side, per the rule in
            `docs/design/layout-typography-color.md`: the looks put up to 6px of
            ink outside a control's border box. `p-` and not `pe-`/`pb-` because
            the vertical pair must stay equal — the header is a fixed 58px with
            `items-center`, and padding on one side only would push the content
            off centre by half of it. The start side eats into the `flex-1`
            spacer and costs nothing; the only visible change is 8px more air
            between this slot and the till controls. */}
        {action ? (
          <div data-ui="topbar-action" className="min-w-0 truncate p-2">
            {action}
          </div>
        ) : null}
        <RestaurantNotificationBell
          triggerClassName={
            isTill
              ? "text-[color:var(--text-secondary)]"
              : "text-[color:var(--text-muted)]"
          }
        />
        {/* Language is on EVERY `Screen`, not only the dark till.
            It used to ride inside `TillControls`, which rendered for
            `tone="dark"` alone — so the login screen, the store-recovery
            screens and the kitchen board had no way to change language at all.
            Login is precisely where a member who reads English needs it, and
            the board is a wall tablet nobody can reach a shell from. The THEME
            toggle stays till-only: `docs/design/layout-typography-color.md`
            scopes dark to `/pos`, and the hook that drives it writes the scope
            class onto `<body>`. */}
        <TillControls
          showLanguage={!floorOnly}
          showTheme={isTill && !floorOnly}
          onLogout={onLogout ?? (floorOnly ? () => setConfirmingLogout(true) : undefined)}
          isLoggingOut={isLoggingOut ?? (floorOnly ? ownLogout.isLoggingOut : undefined)}
        />
      </header>
      {floorOnly ? (
        <ConfirmModal
          isOpen={isConfirmingLogout}
          onClose={() => setConfirmingLogout(false)}
          onConfirm={() => {
            setConfirmingLogout(false);
            ownLogout.logout();
          }}
          title={t((m) => m.pos.logoutConfirmTitle)}
          description={t((m) => m.pos.logoutConfirmBody)}
          confirmLabel={t((m) => m.common.logout)}
          cancelLabel={t((m) => m.common.cancel)}
          closeLabel={t((m) => m.common.close)}
          testId="screen-logout-confirm"
        />
      ) : null}
      <main
        data-app-scroll={bleed ? undefined : true}
        className={bleed ? "flex min-h-0 flex-1 flex-col" : "app-main p-4"}
        aria-labelledby={titleId}
      >
        {children}
      </main>
    </div>
  );
}

/**
 * The bell and the sign-out at the END of a till page's own phone head
 * (`Screen`'s `phoneHead`) — the only chrome the waiter-phone design keeps.
 * Sign-out asks first, as on a floor role's bar: it sits beside the bell.
 */
export function TillPhoneControls({ rule = false }: { rule?: boolean }) {
  const t = useTranslate();
  const { logout, isLoggingOut } = useLogout();
  const [asking, setAsking] = useState(false);
  return (
    <div data-ui="till-phone-controls" className="flex shrink-0 items-center gap-1 lg:hidden">
      {/* A hairline between the page's own controls and these (the design's). */}
      {rule ? <span data-ui="till-phone-rule" aria-hidden="true" className="me-1" /> : null}
      <RestaurantNotificationBell triggerClassName="text-[color:var(--text-secondary)]" />
      <button
        type="button"
        onClick={() => setAsking(true)}
        disabled={isLoggingOut}
        data-testid="till-phone-logout"
        aria-label={t((m) => m.common.logout)}
        title={t((m) => m.common.logout)}
        data-ui="icon-button"
        className="flex h-[34px] w-[34px] items-center justify-center rounded-[var(--radius-tile)] text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--surface-alt)] hover:text-[color:var(--text)] disabled:opacity-50"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="rtl:-scale-x-100">
          <path d="M15 17l5-5-5-5M20 12H9M10 20H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h4" />
        </svg>
      </button>
      <ConfirmModal
        isOpen={asking}
        onClose={() => setAsking(false)}
        onConfirm={() => {
          setAsking(false);
          logout();
        }}
        title={t((m) => m.pos.logoutConfirmTitle)}
        description={t((m) => m.pos.logoutConfirmBody)}
        confirmLabel={t((m) => m.common.logout)}
        cancelLabel={t((m) => m.common.cancel)}
        closeLabel={t((m) => m.common.close)}
        testId="till-phone-logout-confirm"
      />
    </div>
  );
}

/**
 * A `Screen`'s own chrome: language, then theme, then sign-out — the order the
 * redesign's top bar puts them in.
 *
 * It was the TILL's chrome, rendered only for `tone="dark"`, and that gating
 * was the bug: language rode inside it, so the login screen and the kitchen
 * board — the two surfaces whose readers are least likely to be able to fix it
 * themselves — had no language control at all. The pieces are now separately
 * conditional rather than the component being forked in two.
 *
 * Language is a LINK, not a button, because the locale lives in the path: it
 * must be openable in a new tab and it must survive a hard refresh, which a
 * click handler flipping context state would not. The current path is carried
 * across so switching language keeps the reader on the screen they were on.
 *
 * Not named *TopBar/*AppBar/*Shell deliberately — `eslint.no-local-shared-primitives`
 * forbids app-local chrome under those names, and it is right to: these are a
 * screen's controls, not a second shell competing with `Screen`.
 */
function TillControls({
  showLanguage = true,
  showTheme,
  onLogout,
  isLoggingOut,
}: {
  /** Off only on a floor role's till bar (see `Screen`). */
  showLanguage?: boolean;
  /** Till only. See the call site: the hook behind it writes `<body>` scope. */
  showTheme: boolean;
  onLogout?: () => void;
  isLoggingOut?: boolean;
}) {
  const t = useTranslate();
  const { locale } = useI18n();
  const { pathname, search } = useLocation();

  return (
    <div className="flex shrink-0 items-center gap-2">
      {/* ONE control, not a segmented pair: the bar's other controls are
          single 34px buttons, and a language switch is a single act — it
          shows the language you would GET, and takes you there. Swap only the
          FIRST path segment; everything after it is the route the user is on
          and must be preserved. */}
      {showLanguage ? (() => {
        const other = locales.find((code) => code !== locale) ?? locale;
        const next = pathname.replace(/^\/[^/]+/, `/${other}`) + search;
        return (
          <Link
            to={next}
            aria-label={t((m) => m.pos.language)}
            title={t((m) => m.pos.language)}
            data-ui="icon-button"
            data-testid={`locale-${other}`}
            className="flex h-[34px] min-w-[34px] items-center justify-center rounded-[var(--radius-tile)] border border-[color:var(--border)] px-2 text-[length:calc(var(--font-size-2xs)_+_0.5px)] font-bold uppercase text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--surface-alt)] hover:text-[color:var(--text)]"
          >
            {other}
          </Link>
        );
      })() : null}

      {showTheme ? <PosThemeToggle /> : null}

      {onLogout ? (
        <button
          type="button"
          onClick={onLogout}
          disabled={isLoggingOut}
          data-testid="screen-logout"
          aria-label={t((m) => m.common.logout)}
          title={t((m) => m.common.logout)}
          data-ui="icon-button"
          className="flex h-[34px] w-[34px] items-center justify-center rounded-[var(--radius-tile)] border border-[color:var(--border)] text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--surface-alt)] hover:text-[color:var(--text)] disabled:opacity-50"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M15 17l5-5-5-5M20 12H9M10 20H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h4" />
          </svg>
        </button>
      ) : null}
    </div>
  );
}

/**
 * The till's light/dark switch.
 *
 * Split out of `TillControls` so it can be rendered CONDITIONALLY: `usePosTheme`
 * is not a passive read — it puts the `.pos-dark` scope class on `<body>` — so
 * calling it from a component that also renders on the login screen and the
 * kitchen board would paint those dark, which
 * `docs/design/layout-typography-color.md` scopes to `/pos` alone. A hook
 * cannot be called conditionally; a component can be rendered conditionally.
 */
function PosThemeToggle() {
  const t = useTranslate();
  const { theme, toggle } = usePosTheme();

  return (
    <button
      type="button"
      onClick={toggle}
      data-testid="pos-theme-toggle"
      aria-pressed={theme === "dark"}
      aria-label={t((m) => m.pos.theme)}
      title={t((m) => m.pos.theme)}
      data-ui="icon-button"
          className="flex h-[34px] w-[34px] items-center justify-center rounded-[var(--radius-tile)] border border-[color:var(--border)] text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--surface-alt)] hover:text-[color:var(--text)]"
    >
      {theme === "dark" ? (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
        </svg>
      ) : (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
        </svg>
      )}
    </button>
  );
}

/**
 * A row in a list.
 *
 * Deliberately NOT `SectionCard`. `docs/design/components-states-motion.md` is
 * explicit that lists must be denser than forms and that FormCard styling must
 * never be reused for list rows — so this is the rulebook's ListCard, not a
 * fork of a shared primitive. Titled surfaces use `SectionCard` from `@a2z/ui`.
 */
export function ListCard({
  children,
  className,
  tone = "default",
  role,
  padded = true,
}: {
  children: ReactNode;
  className?: string;
  /**
   * `warning` marks a row needing staff attention (buyer requests, conflicts);
   * `danger` marks a row reporting something that has gone wrong or been
   * undone — a failed read that blocks work, a cancelled bill.
   */
  tone?: "default" | "warning" | "danger";
  /** e.g. `alert` for the stale-close recovery, which must announce itself. */
  role?: string;
  /**
   * `false` when the row's whole surface is a single control that must own the
   * padding itself — otherwise the tappable area stops 12px short of the card
   * edge and the row looks bigger than it is. A prop rather than a `p-0` in
   * `className`, because two padding utilities on one element resolve by
   * stylesheet order, not by the order they were written.
   */
  padded?: boolean;
}) {
  return (
    <div
      role={role}
      data-ui="list-card"
      data-tone={tone}
      className={[
        "rounded-[var(--radius-list)] border-[length:var(--border-card-width)] bg-[color:var(--surface)] shadow-[var(--shadow-card)]",
        padded ? "p-3" : "",
        tone === "warning"
          ? "border-[color:var(--warning)]"
          : tone === "danger"
            ? "border-[color:var(--danger)]"
            : "border-[color:var(--border-light)]",
        className ?? "",
      ].join(" ")}
    >
      {children}
    </div>
  );
}

/** Money, formatted for the active locale. */
/**
 * The unit `useMoney` appends, on its own.
 *
 * Exported because a form field wants the same word INSIDE the input, beside a
 * number the user is typing, where a fully formatted string would be wrong.
 * One definition so the label in a field and the value in a list can never
 * disagree about what this store trades in.
 */
export function currencyFor(locale: string): string {
  return locale === "ar" ? "جنيه" : "EGP";
}

export function useMoney() {
  const { locale } = useI18n();
  /**
   * Money, formatted the way the POS redesign formats it, with the currency
   * spelled out and trailing the amount: `320 جنيه` / `320 EGP` — WESTERN
   * digits, and no trailing `.00` on a whole number.
   *
   * Amount first is deliberate. The artboard prefixes its symbol, but a symbol
   * is two glyphs and a spelled-out word is not: leading with `جنيه` pushed the
   * number — the thing actually being read — into second place on every tile
   * and every line of the bill.
   *
   * The Arabic word is `جنيه`, and this constant is the only place it is
   * written — every screen and the printed receipt read it from here. It was
   * `جنية` for a while at the team's request; that is the feminine of `جني`,
   * not the pound, and it printed on the paper handed to every customer. Not a
   * spelling preference, so it is not one to re-open.
   *
   * Two deliberate departures from what this used to do:
   *
   * - It no longer formats through `ar-EG`, which renders Arabic-Indic digits
   *   (٣٢٠). A till is read at a glance and its numbers are compared against a
   *   card terminal, a printed receipt and a cash drawer, all of which show
   *   Western digits here. The artboard uses `toLocaleString('en-US')` for the
   *   same reason.
   * - It no longer pads whole numbers to two decimals. `320` reads as a price;
   *   `٣٢٠٫٠٠` reads as an accounting entry. Fractions still print when a value
   *   actually has them, so nothing is rounded away on screen.
   *
   * This is the money format for the WHOLE restaurant app, receipts included,
   * which is the point — a receipt that disagrees with the till it printed from
   * is worse than either format alone.
   */
  return moneyFormatter(locale);
}

/**
 * A UNIT COST — money per gram, per ml, per piece — at the precision the
 * ledger stores it (4 places, `roundCost`), with the same currency word and
 * Western digits as `useMoney`.
 *
 * Separate from `useMoney` because a unit cost is not an amount: flour at
 * 0.0052 a gram is an ordinary figure, and money's two places print it as
 * "0.01" (or salt at 0.0012 as "0") — a cost that reads 4–40% off, or free.
 * The restock preview promises the average the purchase is about to
 * snapshot, so it has to print that average, not a rounding of it. Totals
 * stay on `useMoney`.
 */
export function useUnitCost() {
  const { locale } = useI18n();
  return unitCostFormatter(locale);
}

/** `useUnitCost` without the hook. */
export function unitCostFormatter(locale: string): (value: number) => string {
  const currency = currencyFor(locale);
  return (value: number) =>
    `${new Intl.NumberFormat("en-US", {
      maximumFractionDigits: 4,
    }).format(value)} ${currency}`;
}

/**
 * `useMoney`'s figure WITHOUT its unit (`currencyFor` is the other half).
 *
 * For a signed run in Arabic: the sign and the digits go in one LTR isolate
 * and the currency word stays outside it, so «جنيه» falls on the same side of
 * a signed figure as of every unsigned one (`docs/design/report-pages.md`).
 */
export function moneyAmount(value: number): string {
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 2,
  }).format(value);
}

/** `useMoney` without the hook — for paper rendered outside React context. */
export function moneyFormatter(locale: string): (value: number) => string {
  const currency = currencyFor(locale);
  return (value: number) => `${moneyAmount(value)} ${currency}`;
}
